import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '../../lib/utils';
import { useDiagramStore } from '../../stores/diagramStore';
import { useToolContext } from '../../hooks/useToolContext';
import type { IToolContext } from '../../core/tools/interfaces/ITool';
import Konva from 'konva';
import { EventBus } from '../../core/events/IEventBus';
import { Separator } from '../ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  MoreVertical,
  RotateCcw,
  ArrowRight,
  Waves,
  Circle,
  Type,
  Trash2,
  Plus,
  ChevronDown,
  Check,
  GripVertical,
} from 'lucide-react';
// removed unused listConnectorManifests, DiagramType
// Removed unused ConnectorMiniIcon
import { getCommandManager } from '../../hooks/useCommandManager';
import { ConnectorEditTool } from '../../domains/genogram/tools/ConnectorEditTool';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { generateDarkModeColorPalette } from '../../react-app/theme/color-utils';
import { RelationshipSubtypeSwitcher } from '../ui/RelationshipSubtypeSwitcher';
import { findSubtypeForEdge, getRelationshipMapping } from '../../lib/diagram/registries';
import type { DiagramType } from '../../lib/diagram/registries';
import {
  attrsWithRelationSemantics,
  relationSemanticsForSubtype,
} from '../../lib/graph/relationshipSemantics';
import { DeleteEdgeCommand } from '../../core/commands/edges/DeleteEdgeCommand';
import { DuplicateEdgeCommand } from '../../core/commands/edges/DuplicateEdgeCommand';
import { UpdateEdgeCommand } from '../../core/commands/edges/UpdateEdgeCommand';
import type { Edge } from '../../core/models/scene';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

type ArrowHead = 'none' | 'triangle' | 'circle' | 'circleOutline' | 'diamond' | 'bar' | 'outlineBlock' | 'backslash';

interface ConnectorToolbarProps {
  selectedEdges: Array<{ id: string } & any>;
  selectionBounds: { x: number; y: number; width: number; height: number };
  // Optional tool context so we can emit events to the active canvas
  toolContext?: IToolContext;
}

const GENOGRAM_EDGE_TYPES = new Set(['marriage', 'child', 'sibling', 'twin-mono', 'twin-di']);
const CONNECTOR_TOOLBAR_GAP = 16;
const CONNECTOR_TOOLBAR_VIEWPORT_PADDING = 12;
const CONNECTOR_TOOLBAR_FALLBACK_SIZE = { width: 560, height: 76 };
// Aligned with ShapeOutlineTool, but without "transparent".
const CONNECTOR_COLOR_PRESETS = [
  ['#F3CE49', '#F2C14B', '#E9A23B', '#CC7B2E'],
  ['#4BA154', '#3982C2', '#4E46DC', '#CC7B2E'],
  ['#8E3B1E', '#8C2822', '#306339', '#263FA9'],
  ['#5425AF', '#6A411C', '#C93B76', '#CA3A31'],
  ['#214D3C', '#253985', '#302E7C', '#672171'],
  ['#E3E8EF', '#97A3B6', '#4D5562', '#000000'],
];

let lastConnectorToolbarDragOffset = { x: 0, y: 0 };

const BPMN_SUBTYPE_I18N_KEY: Readonly<Record<string, string>> = {
  bpmn_message: 'connector.types.bpmnMessageFlow',
  bpmn_sequence: 'connector.types.bpmnSequenceFlow',
  bpmn_default: 'connector.types.bpmnDefaultFlow',
  bpmn_conditional: 'connector.types.bpmnConditionalFlow',
  bpmn_data_association: 'connector.types.bpmnDataAssociation',
};

const isBpmnNode = (node: { type?: string; attrs?: Record<string, unknown> } | undefined): boolean => {
  if (!node) return false;
  if (node.type === 'bpmn-shape') return true;
  const variant = (node.attrs?.variant ?? node.attrs?.shapeType) as string | undefined;
  return typeof variant === 'string' && variant.toLowerCase().startsWith('bpmn-');
};

const resolveConnectorToolbarDiagramType = (
  rawDiagramType: DiagramType | undefined,
  edge: { type?: string; from?: string; to?: string; sourceShapeId?: string; targetShapeId?: string },
  nodes: Array<{ id?: string; type?: string; attrs?: Record<string, unknown> }>,
): DiagramType => {
  if (rawDiagramType && rawDiagramType !== 'genogram') {
    return rawDiagramType;
  }

  const edgeType = edge.type ?? '';
  if (GENOGRAM_EDGE_TYPES.has(edgeType)) {
    return 'genogram';
  }

  const endpointIds = new Set(
    [edge.from, edge.to, edge.sourceShapeId, edge.targetShapeId]
      .filter((value): value is string => typeof value === 'string' && !value.startsWith('__free')),
  );
  const hasGenogramEndpoint = nodes.some((node) => {
    if (!node.id || !endpointIds.has(node.id)) return false;
    return node.type === 'person' || node.attrs?.category === 'genogram';
  });

  return hasGenogramEndpoint ? 'genogram' : 'generic';
};

export const ConnectorToolbar: React.FC<ConnectorToolbarProps> = (props) => {
  if (props.selectedEdges.length === 0) return null;
  return <ConnectorToolbarBody {...props} />;
};

const ConnectorToolbarBody: React.FC<ConnectorToolbarProps> = ({ selectedEdges, selectionBounds, toolContext: externalToolContext }) => {
  const { t } = useTranslation('diagram');
  const selectedIds = useDiagramStore(s => s.selectedIds);
  const nodes = useDiagramStore(s => s.nodes);
  const toolbarRef = React.useRef<HTMLDivElement | null>(null);
  const dragStartPos = React.useRef({ x: 0, y: 0 });
  const [toolbarSize, setToolbarSize] = React.useState(CONNECTOR_TOOLBAR_FALLBACK_SIZE);
  const [dragOffset, setDragOffset] = React.useState(() => lastConnectorToolbarDragOffset);
  const [isDragging, setIsDragging] = React.useState(false);

  const derivedToolCtx = useToolContext({
    stageRef: { current: null as unknown as Konva.Stage },
    layerRef: { current: null as unknown as Konva.Layer },
    eventBus: new EventBus(),
    selectedIds,
    currentFrame: null,
    diagramType: externalToolContext?.diagramType ?? 'genogram',
  });
  const toolCtx = externalToolContext || derivedToolCtx;
  const rawDiagramType: DiagramType | undefined = (toolCtx?.selectionSummary?.diagramType as DiagramType | undefined) || (toolCtx?.diagramType as DiagramType | undefined);
  const stage = useDiagramStore(s => s.stage);
  const { mode } = useThemeMode();
  const [relationshipMenuOpen, setRelationshipMenuOpen] = React.useState(false);
  const selectedEdgeKey = React.useMemo(
    () => selectedEdges.map((selectedEdge) => selectedEdge.id).join('|'),
    [selectedEdges],
  );

  React.useLayoutEffect(() => {
    const element = toolbarRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setToolbarSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      setDragOffset(prev => {
        const next = {
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        };
        lastConnectorToolbarDragOffset = next;
        return next;
      });
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  React.useEffect(() => {
    const resetOffset = { x: 0, y: 0 };
    lastConnectorToolbarDragOffset = resetOffset;
    setDragOffset(resetOffset);
  }, [selectedEdgeKey]);

  const colorPalette = React.useMemo(() => {
    if (mode === 'dark') {
      return generateDarkModeColorPalette(CONNECTOR_COLOR_PRESETS);
    }
    return CONNECTOR_COLOR_PRESETS;
  }, [mode]);

  if (selectedEdges.length === 0) return null;
  const edge = selectedEdges[0] as any;
  const diagramType = resolveConnectorToolbarDiagramType(rawDiagramType, edge, nodes);
  const findRelationshipSubtypeLabel = (edge: { type: string; attrs?: Record<string, unknown> }): string | null => {
    const sub = findSubtypeForEdge(diagramType, edge.type, (edge.attrs || {}) as Record<string, unknown>);
    if (!sub) return null;
    const bpmnKey = BPMN_SUBTYPE_I18N_KEY[sub.id];
    if (bpmnKey) return t(bpmnKey, { defaultValue: sub.label });
    if (sub.label.startsWith('relationships.')) return t(sub.label, sub.label);
    return sub.label;
  };

  const scale = stage.scale;
  const offsetX = stage.offsetX;
  const offsetY = stage.offsetY;
  const boundsLeft = selectionBounds.x * scale + offsetX;
  const boundsTop = selectionBounds.y * scale + offsetY;
  const boundsWidth = Math.max(0, selectionBounds.width * scale);
  const boundsHeight = Math.max(0, selectionBounds.height * scale);
  const boundsBottom = boundsTop + boundsHeight;
  const boundsCenterX = boundsLeft + boundsWidth / 2;
  const viewportWidth = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerHeight;
  const clamp = (value: number, min: number, max: number) => {
    if (!Number.isFinite(max) || max < min) return Math.max(min, value);
    return Math.min(Math.max(value, min), max);
  };
  const chooseDefaultPosition = () => {
    const toolbarWidth = toolbarSize.width || CONNECTOR_TOOLBAR_FALLBACK_SIZE.width;
    const toolbarHeight = toolbarSize.height || CONNECTOR_TOOLBAR_FALLBACK_SIZE.height;
    const aboveY = boundsTop - CONNECTOR_TOOLBAR_GAP - toolbarHeight;
    const belowY = boundsBottom + CONNECTOR_TOOLBAR_GAP;
    const horizontalCenterX = boundsCenterX - toolbarWidth / 2;
    const fitsAbove = aboveY >= CONNECTOR_TOOLBAR_VIEWPORT_PADDING;

    const left = horizontalCenterX;
    const top = fitsAbove ? aboveY : belowY;

    return {
      left: clamp(left, CONNECTOR_TOOLBAR_VIEWPORT_PADDING, viewportWidth - toolbarWidth - CONNECTOR_TOOLBAR_VIEWPORT_PADDING),
      top: clamp(top, CONNECTOR_TOOLBAR_VIEWPORT_PADDING, viewportHeight - toolbarHeight - CONNECTOR_TOOLBAR_VIEWPORT_PADDING),
    };
  };
  const defaultPosition = chooseDefaultPosition();
  const toolbarX = defaultPosition.left + dragOffset.x;
  const toolbarY = defaultPosition.top + dragOffset.y;

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  const relationshipTriggerLabel = findRelationshipSubtypeLabel(edge) || getDefaultLabelForEdge();
  const endArrow: ArrowHead = (toolCtx.selectionStyles?.edgeEndArrow as any) || (edge.attrs?.endArrow as any)?.type || 'triangle';
  const startArrowType: ArrowHead = ((edge.attrs as any)?.startArrow as any)?.type || 'none';
  const isDashed = Boolean(toolCtx.selectionStyles?.edgeDashed ?? (edge.attrs as any)?.dashed);
  const currentRouting = (toolCtx.selectionStyles?.edgeRoutingType as any) || (edge.attrs as any)?.routingType || 'bezier';

  // Route all edits through a tool so changes are undoable
  const commandManager = getCommandManager();
  const connectorEditTool = new ConnectorEditTool();
  const executeEdgeUpdate = async (updates: any) => {
    const cmdOrBatch = connectorEditTool.execute({ edgeId: edge.id, updates });
    if (!cmdOrBatch) return;
    if (Array.isArray(cmdOrBatch)) {
      await commandManager.executeBatch(cmdOrBatch, 'Update Connector');
    } else {
      await commandManager.execute(cmdOrBatch);
    }
  };

  // removed unused setType
  const setEndArrow = (type: ArrowHead) => executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), endArrow: { ...(edge.attrs?.endArrow || {}), type } } });
  const setStartArrow = (type: ArrowHead) => executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), startArrow: { ...(edge.attrs?.startArrow || {}), type } } });
  const swapArrowDirections = () => {
    const s = (edge.attrs as any)?.startArrow || { type: 'none', size: 6 };
    const e = (edge.attrs as any)?.endArrow || { type: 'triangle', size: 8 };
    executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), startArrow: { ...e }, endArrow: { ...s } } });
  };
  const setDash = (dashed: boolean) => executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), dashed } });
  const setRouting = (routingType: 'straight' | 'orthogonal' | 'orthogonalRounded' | 'bezier' | 'rounded') =>
    executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), routingType } });

  // resetConnector removed from UI; keep behavior available via other flows if needed

  const rawLabel = (edge.attrs as any)?.label;
  const hasLabel = typeof rawLabel === 'string' && rawLabel.trim().length > 0;
  const isCaptionHidden = (edge.attrs as any)?.__captionHidden === true || !hasLabel;
  const onAddOrEditText = async () => {
    // Ensure caption is visible before opening inline editor.
    // If a custom label already exists while hidden, preserve it instead of replacing with defaults.
    if (isCaptionHidden) {
      await executeEdgeUpdate({
        attrs: {
          ...(edge.attrs || {}),
          __captionHidden: false,
          ...(hasLabel
            ? {}
            : { label: getDefaultLabelForEdge(), __autoLabel: true, labelOffset: 0.5 }),
        },
      });
    }
    // Ask canvas to open inline editor for this edge label
    try {
      (toolCtx.eventBus as any)?.emit?.('canvas:edge:text:edit', { edgeId: edge.id });
    } catch (err) {
      console.error('Failed to request edge text edit', err);
    }
  };

  function getDefaultLabelForEdge(): string {
    const sub = findSubtypeForEdge(diagramType, edge.type as string, (edge.attrs || {}) as Record<string, unknown>);
    if (sub) {
      const bpmnKey = BPMN_SUBTYPE_I18N_KEY[sub.id];
      if (bpmnKey) return t(bpmnKey, { defaultValue: sub.label });
      if (sub.label.startsWith('relationships.')) return t(sub.label, sub.label);
      return sub.label;
    }
    return t('connector.types.connection');
  }

  // diagram type constant not needed here
  // removed unused connectorTypes
  const arrowIconBox = 'w-4 h-4 flex items-center justify-center';
  const arrowHeads: Array<{ id: ArrowHead; label: string; icon?: React.ReactNode }> = [
    { id: 'none', label: t('connector.arrowHeads.none'), icon: <div className={`${arrowIconBox}`}><div className="w-3 h-3 border border-gray-400 rounded" /></div> },
    { id: 'triangle', label: t('connector.arrowHeads.triangle'), icon: <div className={arrowIconBox}><ArrowRight className="w-3 h-3" /></div> },
    { id: 'circle', label: t('connector.arrowHeads.circle'), icon: <div className={arrowIconBox}><div className="w-3 h-3 rounded-full border-2 border-gray-700" /></div> },
    { id: 'circleOutline', label: t('connector.arrowHeads.circleOutline'), icon: <div className={arrowIconBox}><div className="w-3 h-3 rounded-full border-2 border-gray-700 bg-white" /></div> },
    { id: 'diamond', label: t('connector.arrowHeads.diamond'), icon: <div className={arrowIconBox}><div className="w-3 h-3 rotate-45 border-2 border-gray-700" /></div> },
    { id: 'bar', label: t('connector.arrowHeads.bar'), icon: <div className={arrowIconBox}><div className="w-[2px] h-3 bg-gray-700" /></div> },
    { id: 'outlineBlock', label: t('connector.arrowHeads.outlineBlock'), icon: <div className={arrowIconBox}><svg width="12" height="12" viewBox="0 0 12 12"><path d="M0 0 L12 6 L0 12 Z" fill="white" stroke="currentColor" strokeWidth="1.5" /></svg></div> },
    { id: 'backslash', label: t('connector.arrowHeads.backslash'), icon: <div className={arrowIconBox}><svg width="12" height="12" viewBox="0 0 12 12"><line x1="12" y1="0" x2="0" y2="12" stroke="currentColor" strokeWidth="2" /></svg></div> },
  ];

  const canEditGeometry = edge.type !== 'marriage' && edge.type !== 'child';
  const isBpmnSequenceFlow = diagramType === 'flowchart' && edge.type === 'sequence_flow';
  const isGenogramConnector = diagramType === 'genogram';
  const canEditArrow = canEditGeometry && !isGenogramConnector;

  const fromNode = nodes.find((n: any) => n.id === (edge.from ?? (edge as any).sourceShapeId));
  const toNode = nodes.find((n: any) => n.id === (edge.to ?? (edge as any).targetShapeId));
  const hasImageEndpoint = fromNode?.type === 'image' || toNode?.type === 'image';
  const isBpmnConnectorContext =
    isBpmnNode(fromNode) ||
    isBpmnNode(toNode) ||
    (findSubtypeForEdge(diagramType, edge.type, (edge.attrs || {}) as Record<string, unknown>)?.id?.startsWith?.('bpmn_'));

  // Distinguish between:
  // 1. New "Line" shapes (from shape picker): both endpoints free with fromPoint and toPoint
  // 2. Existing connector lines (from shape connection points): at least one endpoint connected to a shape
  const isFreeLine = edge.type === 'line' &&
    edge.from?.startsWith('__freeF:') &&
    edge.to?.startsWith('__freeT:') &&
    (edge.attrs as any)?.fromPoint &&
    (edge.attrs as any)?.toPoint;

  const ORG_EDGE_TYPES = new Set(['reporting', 'dotted', 'matrix', 'team', 'occupies']);
  const GENOGRAM_RELATIONSHIP_EDGE_TYPES = new Set<string>(['line', ...Array.from(GENOGRAM_EDGE_TYPES)]);
  const PROCESS_EDGE_TYPES = new Set(['sequence_flow']);

  // In genogram, many relationship subtypes map to `edgeType: "line"` (emotional/abuse/etc),
  // so treat non-free `line` connectors as relationship-capable and show the dropdown.
  const isRelationshipCapableEdge =
    (diagramType === 'genogram' && GENOGRAM_RELATIONSHIP_EDGE_TYPES.has(edge.type)) ||
    (diagramType === 'org' && ORG_EDGE_TYPES.has(edge.type)) ||
    (diagramType === 'flowchart' && PROCESS_EDGE_TYPES.has(edge.type));
  const shouldShowRelationshipSwitcher = isRelationshipCapableEdge && !hasImageEndpoint;
  const isPlainLine = edge.type === 'line' && !isRelationshipCapableEdge;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        ref={toolbarRef}
        data-testid="connector-toolbar"
        className={cn(
          'shape-toolbar',
          'absolute z-50 bg-background rounded-[10px] border border-border shadow-[0_6px_18px_rgba(2,6,23,0.08)] py-1.5 pr-2 flex items-center gap-1 min-w-fit'
        )}
        style={{ left: toolbarX, top: toolbarY }}
      >
        <div
          className={cn(
            'self-stretch flex items-center justify-center py-2 pl-1.5 pr-1 bg-muted/40 rounded-l-[10px]',
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          )}
          onMouseDown={handleDragStart}
          role="button"
          tabIndex={0}
          aria-label={t('shapeToolbar.dragToReposition', { defaultValue: 'Drag to reposition toolbar' })}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <Separator orientation="vertical" className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] mr-1" />

        {/* BPMN connector type switcher - only when connector is in BPMN context (connects BPMN shapes or already has BPMN subtype) */}
        {!isFreeLine && diagramType === 'flowchart' && isBpmnConnectorContext && (edge.type === 'sequence_flow' || edge.type === 'line') && (
          (() => {
            const BPMN_OPTIONS = [
              { id: 'line', label: t('connector.types.line', { defaultValue: 'Line' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><path d="M2 4 L22 4" stroke="currentColor" strokeWidth="1.5" /><path d="M18 2 L24 4 L18 6 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" /></svg> },
              { id: 'bpmn_message', label: t('connector.types.bpmnMessageFlow', { defaultValue: 'Message Flow' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><circle cx="4" cy="4" r="2" fill="white" stroke="currentColor" strokeWidth="1.2" /><path d="M8 4 L18 4" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" /><path d="M20 2 L26 4 L20 6 Z" fill="white" stroke="currentColor" strokeWidth="1.2" /></svg> },
              { id: 'bpmn_sequence', label: t('connector.types.bpmnSequenceFlow', { defaultValue: 'Sequence Flow' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><path d="M2 4 L22 4" stroke="currentColor" strokeWidth="1.5" /><path d="M18 2 L24 4 L18 6 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" /></svg> },
              { id: 'bpmn_default', label: t('connector.types.bpmnDefaultFlow', { defaultValue: 'Default Flow' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><line x1="2" y1="6" x2="6" y2="2" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4 L22 4" stroke="currentColor" strokeWidth="1.5" /><path d="M18 2 L24 4 L18 6 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" /></svg> },
              { id: 'bpmn_conditional', label: t('connector.types.bpmnConditionalFlow', { defaultValue: 'Conditional Flow' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><path d="M4 4 L6 2 L8 4 L6 6 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" /><path d="M10 4 L22 4" stroke="currentColor" strokeWidth="1.5" /><path d="M18 2 L24 4 L18 6 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" /></svg> },
              { id: 'bpmn_data_association', label: t('connector.types.bpmnDataAssociation', { defaultValue: 'Data Association' }), icon: <svg className="w-5 h-4" viewBox="0 0 28 8"><path d="M2 4 L22 4" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" /><path d="M18 2 L24 4 L18 6" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg> },
            ];
            const currentSubtype = findSubtypeForEdge(diagramType, edge.type, (edge.attrs || {}) as Record<string, unknown>);
            const isPlainLineConnector = edge.type === 'line';
            const currentLabel = isPlainLineConnector
              ? t('connector.types.line', { defaultValue: 'Line' })
              : currentSubtype
                ? BPMN_SUBTYPE_I18N_KEY[currentSubtype.id]
                  ? t(BPMN_SUBTYPE_I18N_KEY[currentSubtype.id], { defaultValue: currentSubtype.label })
                  : currentSubtype.label
                : t('connector.types.bpmnSequenceFlow', { defaultValue: 'Sequence Flow' });
            const BPMN_FALLBACK_MAPPINGS: Record<string, { edgeType: string; defaultEdgeProps: Record<string, unknown> }> = {
              bpmn_message: { edgeType: 'sequence_flow', defaultEdgeProps: { dashed: true, dash: [8, 4], startArrow: { type: 'circleOutline', size: 6 }, endArrow: { type: 'outlineBlock', size: 10 }, color: '#334155' } },
              bpmn_sequence: { edgeType: 'sequence_flow', defaultEdgeProps: { dashed: false, dash: undefined, startArrow: { type: 'none', size: 6 }, endArrow: { type: 'triangle', size: 8 }, color: '#334155' } },
              bpmn_default: { edgeType: 'sequence_flow', defaultEdgeProps: { dashed: false, dash: undefined, startArrow: { type: 'backslash', size: 6 }, endArrow: { type: 'triangle', size: 8 }, color: '#334155' } },
              bpmn_conditional: { edgeType: 'sequence_flow', defaultEdgeProps: { dashed: false, dash: undefined, startArrow: { type: 'diamond', size: 8 }, endArrow: { type: 'triangle', size: 8 }, color: '#334155' } },
              bpmn_data_association: { edgeType: 'sequence_flow', defaultEdgeProps: { dashed: true, dash: [4, 4], startArrow: { type: 'none', size: 6 }, endArrow: { type: 'chevron', size: 7 }, color: '#475569' } },
            };
            const handleBpmnSwitch = (subtypeId: string) => {
              if (subtypeId === 'line') {
                const lineAttrs = {
                  ...(edge.attrs || {}),
                  color: '#334155',
                  width: 2,
                  dashed: false,
                  endArrow: { type: 'triangle', size: 8 },
                  startArrow: { type: 'none', size: 6 },
                  routingType: (edge.attrs as any)?.routingType || 'orthogonalRounded',
                };
                const cmd = new UpdateEdgeCommand(edge.id, { type: 'line', attrs: lineAttrs });
                commandManager.execute(cmd);
                return;
              }
              const mapping = getRelationshipMapping(diagramType, subtypeId) ?? BPMN_FALLBACK_MAPPINGS[subtypeId];
              if (!mapping) return;
              const { edgeType, defaultEdgeProps } = mapping;
              const mergedAttrs = { ...(edge.attrs || {}), ...(defaultEdgeProps as any) };
              const semantics = relationSemanticsForSubtype({
                diagramType,
                subtypeId,
                qualifierSource: mergedAttrs,
                originalLabel: subtypeId,
              });
              const cmd = new UpdateEdgeCommand(edge.id, {
                type: edgeType as Edge['type'],
                attrs: attrsWithRelationSemantics(mergedAttrs, semantics, subtypeId),
              });
              commandManager.execute(cmd);
            };
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="connector-bpmn-type"
                    className="flex items-center gap-1 px-2 h-8 rounded hover:bg-muted transition-colors"
                  >
                    <span className="text-sm text-foreground truncate max-w-[110px]">{currentLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[320px] p-2">
                  {BPMN_OPTIONS.map((opt) => {
                    const isActive = opt.id === 'line' ? edge.type === 'line' : currentSubtype?.id === opt.id;
                    return (
                      <DropdownMenuItem
                        key={opt.id}
                        data-testid={`tid.editor.connector.bpmnType.${opt.id}`}
                        onClick={() => handleBpmnSwitch(opt.id)}
                        className={cn('flex items-center gap-3 px-3 py-2 cursor-pointer', isActive && 'font-medium bg-accent')}
                      >
                        <span className="flex items-center gap-1">{opt.icon}</span>
                        <span className="text-[14px] text-foreground">{opt.label}</span>
                        {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()
        )}

        {/* Start/Swap/End arrow controls */}
        {canEditArrow && (
          <>
            {/* Start type dropdown */}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="connector-start-type"
                      className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                    >
                      <ArrowRight className="w-4 h-4 rotate-180 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('connector.startType')}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-[180px]">
                {arrowHeads.map((a) => (
                  <DropdownMenuItem
                    key={`start-${a.id}`}
                    onClick={() => setStartArrow(a.id)}
                    className={cn('flex items-center gap-2', startArrowType === a.id ? 'font-medium bg-accent' : '')}
                    data-testid={`connector-start-arrow-${a.id}`}
                  >
                    {a.icon}
                    {a.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="connector-swap-arrows"
                  className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                  onClick={swapArrowDirections}
                >
                  <RotateCcw className="w-4 h-4 text-muted-foreground rotate-180" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('connector.swapArrows')}</TooltipContent>
            </Tooltip>

            {/* End type dropdown */}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="connector-end-type"
                      className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                    >
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('connector.endType')}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-[180px]">
                {arrowHeads.map((a) => (
                  <DropdownMenuItem
                    key={`end-${a.id}`}
                    onClick={() => setEndArrow(a.id)}
                    className={cn('flex items-center gap-2', endArrow === a.id ? 'font-medium bg-accent' : '')}
                    data-testid={`connector-end-arrow-${a.id}`}
                  >
                    {a.icon}
                    {a.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        {/* Line style button (omit for marriage/child and free lines) */}
        {canEditGeometry && !isFreeLine && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="connector-line-style"
                    className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                  >
                    <Waves className="w-4 h-4 text-gray-600" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('connector.lineStyle')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="w-[200px]">
              <DropdownMenuItem
                onClick={() => setRouting('bezier')}
                className={currentRouting === 'bezier' ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-routing-bezier"
              >
                {t('connector.routing.bezier')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setRouting('straight')}
                className={currentRouting === 'straight' ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-routing-straight"
              >
                {t('connector.routing.straight')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setRouting('orthogonal')}
                className={currentRouting === 'orthogonal' ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-routing-orthogonal"
              >
                {t('connector.routing.orthogonal')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setRouting('orthogonalRounded')}
                className={currentRouting === 'orthogonalRounded' ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-routing-orthogonal-rounded"
              >
                {t('connector.routing.orthogonalRounded')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setRouting('rounded')}
                className={currentRouting === 'rounded' ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-routing-rounded"
              >
                {t('connector.routing.rounded')}
              </DropdownMenuItem>
              <Separator className="my-1" />
              <DropdownMenuItem
                onClick={() => setDash(!isDashed)}
                className={isDashed ? 'font-medium bg-blue-50' : ''}
                data-testid="connector-toggle-dash"
              >
                {isDashed ? t('connector.lineStyles.solidLine') : t('connector.lineStyles.dashedLine')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Line color, thickness and style (combined panel) */}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="connector-line-color"
                  className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                  aria-label={t('connector.lineColor')}
                >
                  <Circle
                    className="w-4 h-4"
                    style={{ color: (edge.attrs?.color as string) || '#334155' }}
                    fill="currentColor"
                  />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('connector.lineColor')}</TooltipContent>
          </Tooltip>
          {
            // Helper utilities co-located to keep the change minimal
          }
          {(() => {
            // Infer current state from edge
            const currentColor: string = (edge.attrs?.color as string) || '#334155';
            const currentWidth: number = typeof (edge.attrs as any)?.width === 'number' ? (edge.attrs as any).width : 2;
            const dashed: boolean = Boolean((edge.attrs as any)?.dashed);
            const dash: number[] = Array.isArray((edge.attrs as any)?.dash) ? ((edge.attrs as any).dash as number[]) : [];

            type StrokeStyle = 'solid' | 'dashed' | 'dotted' | 'dash-dot';
            const resolveStyle = (isDashed: boolean, d: number[]): StrokeStyle => {
              if (!isDashed || !d || d.length === 0) return 'solid';
              const approx = (a: number, b: number, t = 1) => Math.abs(a - b) <= t;
              const [a, b, c, e] = [...d, undefined, undefined, undefined].slice(0, 4) as any[];
              if (d.length >= 4 && (approx(a, 8, 2) || approx(a, 10, 2)) && approx(b, 4, 1) && approx(c, 2, 1) && approx(e, 4, 1)) return 'dash-dot';
              if (d.length >= 2 && (approx(a, 2, 1) && approx(b, 4, 1))) return 'dotted';
              if (d.length >= 2 && ((approx(a, 8, 2) && approx(b, 4, 1)) || (approx(a, 10, 2) && approx(b, 5, 2)))) return 'dashed';
              return 'dashed';
            };
            const currentStyle: StrokeStyle = resolveStyle(dashed, dash);

            const styleToDash = (style: StrokeStyle): { dashed: boolean; dash: number[] } => {
              if (style === 'solid') return { dashed: false, dash: [] };
              if (style === 'dotted') return { dashed: true, dash: [2, 4] };
              if (style === 'dash-dot') return { dashed: true, dash: [8, 4, 2, 4] };
              return { dashed: true, dash: [8, 4] };
            };

            const apply = (updates: { color?: string; width?: number; style?: StrokeStyle }) => {
              const attrs = { ...(edge.attrs || {}) } as any;
              if (updates.color) attrs.color = updates.color;
              if (typeof updates.width === 'number') attrs.width = updates.width;
              if (updates.style) {
                const m = styleToDash(updates.style);
                attrs.dashed = m.dashed;
                attrs.dash = m.dash;
              }
              executeEdgeUpdate({ attrs });
            };

            const strokeStyles: Array<{ value: StrokeStyle; label: string; dash?: number[] }> = [
              { value: 'solid', label: t('connector.lineStyles.solid') },
              { value: 'dashed', label: t('connector.lineStyles.dashed'), dash: [8, 4] },
              { value: 'dotted', label: t('connector.lineStyles.dotted'), dash: [2, 4] },
              { value: 'dash-dot', label: t('connector.lineStyles.dashDot'), dash: [8, 4, 2, 4] },
            ];

            const thicknessOptions = [1, 2, 3, 4, 6, 8];

            return (
              <DropdownMenuContent align="start" className="w-[280px] p-3">
                {/* Header */}
                <div className="mb-3 pb-2 border-b border-border">
                  <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('connector.lineColor')}</h3>
                </div>
                <div className="flex gap-3">
                  {/* Left: color grid */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      {colorPalette.flat().map((color, index) => {
                        const selected = color.toLowerCase() === String(currentColor).toLowerCase();
                        return (
                          <button
                            type="button"
                            key={`${color}-${index}`}
                            className="relative flex h-6 w-6 items-center justify-center rounded-full border transition hover:scale-110"
                            style={{ backgroundColor: color, borderColor: 'rgba(148, 163, 184, 0.45)' }}
                            aria-label={t('properties.applyColor', { color, defaultValue: 'Apply color {{color}}' })}
                            aria-pressed={selected}
                            data-testid={`connector-color-${color.replace('#', '')}`}
                            onClick={() => apply({ color })}
                          />
                        );
                      })}
                    </div>

                    {/* Custom color row with + button */}
                    <div className="grid grid-cols-4 gap-2 pt-1">
                      <button
                        type="button"
                        data-testid="tid.editor.connector.swatch.custom"
                        onClick={() => document.getElementById('connector-color-picker')?.click()}
                        className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                        aria-label={t('connector.pickCustomColor')}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                      <input
                        id="connector-color-picker"
                        type="color"
                        value={(typeof currentColor === 'string' && currentColor.startsWith('#')) ? currentColor : '#334155'}
                        onChange={(e) => apply({ color: e.target.value })}
                        className="absolute h-px w-px opacity-0"
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="w-px self-stretch bg-border/80" />

                  {/* Right: thickness + style */}
                  <div className="space-y-3 flex-1">
                    {/* Thickness */}
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-medium text-muted-foreground">{t('connector.thickness')}</div>
                      <div className="grid grid-cols-4 gap-1">
                        {thicknessOptions.map((w) => {
                          const selected = currentWidth === w;
                          return (
                            <button
                              key={w}
                              data-testid={`tid.editor.connector.swatch.width.${w}`}
                              onClick={() => apply({ width: w })}
                              className={`flex h-7 items-center justify-center rounded transition ${selected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                              aria-label={t('connector.width', { width: w })}
                            >
                              <div
                                className="rounded-full bg-foreground"
                                style={{ width: Math.min(w * 1.5 + 3, 12), height: Math.min(w * 1.5 + 3, 12) }}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Style */}
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-medium text-muted-foreground">{t('connector.style')}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {strokeStyles.map((s) => (
                          <button
                            key={s.value}
                            data-testid={`tid.editor.connector.swatch.style.${s.value}`}
                            onClick={() => apply({ style: s.value })}
                            className={`h-7 px-2 rounded flex items-center justify-center transition ${currentStyle === s.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            aria-label={s.label}
                          >
                            <svg width="28" height="3" viewBox="0 0 28 3" className="text-current">
                              {s.value === 'solid' && (
                                <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" />
                              )}
                              {s.value === 'dashed' && (
                                <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="5 2" />
                              )}
                              {s.value === 'dotted' && (
                                <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="1 2" />
                              )}
                              {s.value === 'dash-dot' && (
                                <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="5 2 1 2" />
                              )}
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </DropdownMenuContent>
            );
          })()}
        </DropdownMenu>

        {/* Relationship/style switcher (omit for free lines, non-relationship connectors, and BPMN sequence_flow) */}
        {!isFreeLine && shouldShowRelationshipSwitcher && !isPlainLine && !isBpmnSequenceFlow && (
          <DropdownMenu open={relationshipMenuOpen} onOpenChange={setRelationshipMenuOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="connector-relationship-style"
                    className="flex items-center gap-1 px-2 h-8 rounded hover:bg-muted transition-colors"
                  >
                    <span className="text-sm text-foreground truncate max-w-[110px]">
                      {relationshipTriggerLabel}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('connector.relationship')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="p-0 w-[300px] overflow-hidden" data-testid="tid.ui.relationship.menu">
              <RelationshipSubtypeSwitcher
                diagramType={diagramType as any}
                selectedEdgeIds={[edge.id]}
                variant="list"
                onExecute={(cmds) => {
                  const commandManager = getCommandManager();
                  commandManager.executeBatch(cmds, 'Change Relationship Type');
                  setRelationshipMenuOpen(false);
                }}
                onClose={() => setRelationshipMenuOpen(false)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Simple "Line" label for plain line connectors (omit when BPMN connector type dropdown is shown) */}
        {!isFreeLine && isPlainLine && !(diagramType === 'flowchart' && isBpmnConnectorContext && (edge.type === 'sequence_flow' || edge.type === 'line')) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-1 px-2 h-8 text-sm text-foreground">
                {t('connector.types.line')}
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('connector.types.genericLine')}</TooltipContent>
          </Tooltip>
        )}

        {/* Text label (omit for free lines) */}
        {!isFreeLine && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="connector-label"
                className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                onClick={onAddOrEditText}
              >
                <Type className="w-4 h-4 text-gray-600" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{hasLabel ? t('connector.editLabel') : t('connector.addLabel')}</TooltipContent>
          </Tooltip>
        )}

        {/* Lock removed for now (not supported) */}

        {/* More options */}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="connector-more"
                  className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('connector.more')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-[220px]">
            {/* Hide caption option (omit for free lines) */}
            {!isFreeLine && (
              <DropdownMenuItem data-testid="tid.editor.connector.menu.captionToggle" onClick={() => {
                const hidden = (edge.attrs as any)?.__captionHidden === true || !hasLabel;
                if (hidden) {
                  // Show caption: if a label already exists, reveal it; otherwise set a sensible default.
                  executeEdgeUpdate({
                    attrs: {
                      ...(edge.attrs || {}),
                      __captionHidden: false,
                      ...(hasLabel ? {} : { label: getDefaultLabelForEdge(), __autoLabel: true }),
                    },
                  });
                } else {
                  // Hide caption: suppress auto-render and clear explicit label
                  executeEdgeUpdate({ attrs: { ...(edge.attrs || {}), __captionHidden: true, label: undefined } });
                }
              }}>
                <Type className="w-4 h-4 mr-2" />
                {isCaptionHidden ? t('connector.showCaption') : t('connector.hideCaption')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid="tid.editor.connector.menu.duplicate" onClick={() => {
              const cmd = new DuplicateEdgeCommand(edge.id);
              commandManager.execute(cmd);
            }}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('connector.duplicate')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.connector.menu.delete" className="text-red-600" onClick={() => {
              const cmd = new DeleteEdgeCommand(edge.id);
              commandManager.execute(cmd);
            }}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t('connector.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
};
